import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import "./ArticleDetails.css";
import Breadcrumb from './Breadcrumb';

const ArticleDetail = () => {
    const { slug: slugParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [article, setArticle] = useState(null);
    const [recentArticles, setRecentArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageKey, setImageKey] = useState(0);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatMetaDate = (category, dateString) => {
        return `${category} | ${formatDate(dateString)}`;
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    };

    const formatText = (text) => {
        if (!text) return '';
        const bulletRegex = /^[·•\-]\s*(.+)$/gm;
        const hasBullets = bulletRegex.test(text);

        if (hasBullets) {
            const lines = text.split('\n');
            let beforeText = '';
            let bulletItems = [];
            let afterText = '';
            let inBullets = false;

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                const isBullet = /^[·•\-]/.test(line);
                if (isBullet) {
                    inBullets = true;
                    const itemText = line.replace(/^[·•\-]\s*/, '');
                    bulletItems.push(`<li>${itemText}</li>`);
                } else if (inBullets && !isBullet && line === '') {
                    inBullets = false;
                } else if (!inBullets) {
                    if (bulletItems.length === 0) {
                        beforeText += line + ' ';
                    } else {
                        afterText += line + ' ';
                    }
                }
            }

            let result = '';
            if (beforeText.trim()) result += `<p>${beforeText.trim()}</p>`;
            if (bulletItems.length > 0) result += `<ul class="custom-list">${bulletItems.join('')}</ul>`;
            if (afterText.trim()) result += `<p>${afterText.trim()}</p>`;
            return result;
        }

        const paragraphs = text.split(/\n\s*\n/);
        return paragraphs.map(p => {
            if (p.trim()) {
                const cleanParagraph = p.replace(/\n/g, ' ').trim();
                return `<p>${cleanParagraph}</p>`;
            }
            return '';
        }).join('');
    };

    useEffect(() => {
        setLoading(true);

        // Récupérer les données depuis location.state
        if (location.state?.articleData) {
            console.log("✅ Données reçues depuis Blog.js");
            setArticle(location.state.articleData);
            setImageKey(prev => prev + 1);
        } else {
            setError("Aucun article trouvé");
        }

        // Récupérer les articles récents depuis location.state
        if (location.state?.recentArticles) {
            setRecentArticles(location.state.recentArticles);
        }

        setLoading(false);
        scrollToTop();
    }, [location.state, slugParam]);

    const handleRecentArticleClick = (recentArticle) => {
        navigate(`/blog/${recentArticle.slug}`, {
            state: {
                articleData: {
                    id: recentArticle.id,
                    title: recentArticle.title,
                    content: recentArticle.content,
                    banner: recentArticle.banner,
                    articleCategory: recentArticle.articleCategory,
                    date: recentArticle.date,
                    slug: recentArticle.slug,
                    url: recentArticle.url,
                    description: recentArticle.excerpt
                },
                recentArticles: recentArticles
            }
        });
        scrollToTop();
    };

    const handleImageError = (e) => {
        e.target.style.display = 'none';
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Chargement de l'article...</p>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="error">
                <h2>Article non trouvé</h2>
                <p>{error || "L'article que vous recherchez n'existe pas."}</p>
                <Link to="/blog" className="back-link">← Retour aux articles</Link>
            </div>
        );
    }

    return (
        <>
            <Breadcrumb />
            <div className="article-detail-container">
                <div className="article-meta">
                    <span className="article-date">
                        {formatMetaDate(article.articleCategory, article.date)}
                    </span>
                </div>

                <div className="article-detail-content">
                    <div className="article-layout">
                        <div className="article-main">
                            <article className="article-full">
                                <h1 className="article-title">{article.title}</h1>
                                <span className="publish-date">
                                    Publié le {formatDate(article.date)}
                                </span>

                                {article.banner ? (
                                    <div className="article-banner">
                                        <img
                                            key={`main-${article.id}-${imageKey}`}
                                            src={article.banner}
                                            alt={article.title}
                                            loading="lazy"
                                            onError={handleImageError}
                                        />
                                    </div>
                                ) : (
                                    <div className="article-banner no-image-placeholder">
                                        <div className="no-image">Aucune image disponible</div>
                                    </div>
                                )}

                                <div className="article-content">
                                    {article.content ? (
                                        <div
                                            className="article-html-content"
                                            dangerouslySetInnerHTML={{ __html: formatText(article.content) }}
                                        />
                                    ) : (
                                        <div className="no-content">
                                            {/* <p>{article.description || "Aucun contenu disponible."}</p> */}
                                        </div>
                                    )}
                                </div>
                            </article>
                        </div>

                        <div className="recent-articles-sidebar">
                            <h3 className="sidebar-title">Articles récents</h3>
                            <div className="recent-articles-list">
                                {recentArticles.slice(0, 4).map((recentArticle) => (
                                    <div
                                        key={recentArticle.id}
                                        className="recent-article-item"
                                        onClick={() => handleRecentArticleClick(recentArticle)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="recent-article-content">
                                            <h4 className="recent-article-title">
                                                {recentArticle.title}
                                            </h4>
                                        </div>
                                    </div>
                                ))}

                                {recentArticles.length === 0 && (
                                    <p className="no-recent-articles">Aucun article récent</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ArticleDetail;